import * as turf from '@turf/turf';

export const getPolygonType = (properties, fallback = 'N') => {
    const knownTypes = ['N', 'T', 'F', 'X', 'R'];
    const candidates = [properties && properties.poly_type, properties && properties.type];
    for (const candidate of candidates) {
        const type = String(candidate || '').trim().toUpperCase();
        if (knownTypes.includes(type)) return type;
    }
    return fallback;
};

const isFiniteNumber = value => {
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }

    if (typeof value !== 'string' || value.trim() === '') {
        return false;
    }

    return Number.isFinite(Number(value));
};

const interpolateColor = (start, end, ratio) => start.map(
    (channel, index) => Math.round(channel + ((end[index] - channel) * ratio))
);

const toHexColor = channels => `#${channels
    .map(channel => channel.toString(16).padStart(2, '0'))
    .join('')}`;

export const getValueColor = (value, min, max) => {
    const numericValue = Number(value);
    const numericMin = Number(min);
    const numericMax = Number(max);
    const green = [0, 176, 80];
    const yellow = [255, 215, 0];
    const red = [211, 47, 47];

    if (
        !Number.isFinite(numericValue) ||
        !Number.isFinite(numericMin) ||
        !Number.isFinite(numericMax)
    ) {
        return '#2f80ed';
    }

    if (numericMax <= numericMin) {
        return toHexColor(green);
    }

    const position = Math.min(
        1,
        Math.max(0, (numericValue - numericMin) / (numericMax - numericMin))
    );

    if (position <= 0.5) {
        return toHexColor(interpolateColor(green, yellow, position * 2));
    }

    return toHexColor(interpolateColor(yellow, red, (position - 0.5) * 2));
};

export const getInitialRangeSeparation = (min, max) => {
    const difference = Math.max(0, Math.round(Number(max) - Number(min)));

    if (difference === 0) {
        return 0;
    }

    return Math.min(
        difference,
        Math.max(1, Math.round(difference * 0.15))
    );
};

export const convertMultiPolygonsToPolygons = features => (features || [])
    .reduce((result, feature) => {
        if (
            !feature ||
            !feature.geometry ||
            feature.geometry.type !== 'MultiPolygon'
        ) {
            result.push(feature);
            return result;
        }

        feature.geometry.coordinates.forEach((coordinates, index) => {
            result.push({
                ...feature,
                id: typeof feature.id === 'undefined'
                    ? undefined
                    : `${feature.id}-${index + 1}`,
                properties: {
                    ...(feature.properties || {})
                },
                geometry: {
                    type: 'Polygon',
                    coordinates
                }
            });
        });

        return result;
    }, []);

const coordinatesAreEqual = (first, second) =>
    Array.isArray(first) &&
    Array.isArray(second) &&
    first.length >= 2 &&
    second.length >= 2 &&
    first[0] === second[0] &&
    first[1] === second[1];

const BOUNDARY_VERTEX_TOLERANCE = 1e-12;

const getCoordinateKey = coordinate => `${coordinate[0]}:${coordinate[1]}`;

const collectOriginalVertices = features => {
    const vertices = new Map();

    (features || []).forEach(feature => {
        const geometry = feature && feature.geometry;

        if (!geometry) {
            return;
        }

        const polygons = geometry.type === 'Polygon'
            ? [geometry.coordinates]
            : (geometry.type === 'MultiPolygon' ? geometry.coordinates : []);

        polygons.forEach(rings => {
            (rings || []).forEach(ring => {
                (ring || []).forEach(coordinate => {
                    if (
                        Array.isArray(coordinate) &&
                        coordinate.length >= 2 &&
                        Number.isFinite(Number(coordinate[0])) &&
                        Number.isFinite(Number(coordinate[1]))
                    ) {
                        const point = [Number(coordinate[0]), Number(coordinate[1])];
                        vertices.set(getCoordinateKey(point), point);
                    }
                });
            });
        });
    });

    return Array.from(vertices.values());
};

const getPositionOnSegment = (point, start, end) => {
    const deltaX = end[0] - start[0];
    const deltaY = end[1] - start[1];
    const lengthSquared = (deltaX * deltaX) + (deltaY * deltaY);

    if (lengthSquared === 0) {
        return null;
    }

    const tolerance = BOUNDARY_VERTEX_TOLERANCE;

    if (
        point[0] < Math.min(start[0], end[0]) - tolerance ||
        point[0] > Math.max(start[0], end[0]) + tolerance ||
        point[1] < Math.min(start[1], end[1]) - tolerance ||
        point[1] > Math.max(start[1], end[1]) + tolerance
    ) {
        return null;
    }

    const position = (
        ((point[0] - start[0]) * deltaX) +
        ((point[1] - start[1]) * deltaY)
    ) / lengthSquared;

    if (position <= 0 || position >= 1) {
        return null;
    }

    const projectedX = start[0] + (position * deltaX);
    const projectedY = start[1] + (position * deltaY);
    const distanceX = point[0] - projectedX;
    const distanceY = point[1] - projectedY;

    return ((distanceX * distanceX) + (distanceY * distanceY)) <= tolerance * tolerance
        ? position
        : null;
};

const restoreRingVertices = (ring, originalVertices) => {
    if (!Array.isArray(ring) || ring.length < 2) {
        return ring;
    }

    const restored = [];

    for (let index = 0; index < ring.length - 1; index++) {
        const start = ring[index];
        const end = ring[index + 1];
        const pointsOnSegment = [];

        restored.push(start);

        originalVertices.forEach(point => {
            if (coordinatesAreEqual(point, start) || coordinatesAreEqual(point, end)) {
                return;
            }

            const position = getPositionOnSegment(point, start, end);

            if (position !== null) {
                pointsOnSegment.push({ point, position });
            }
        });

        pointsOnSegment
            .sort((first, second) => first.position - second.position)
            .forEach(({ point }) => {
                const last = restored[restored.length - 1];

                if (!coordinatesAreEqual(last, point)) {
                    restored.push(point.slice());
                }
            });
    }

    const last = ring[ring.length - 1];

    if (!coordinatesAreEqual(restored[restored.length - 1], last)) {
        restored.push(last);
    }

    return restored;
};

const restoreOriginalBoundaryVertices = (feature, sourceFeatures) => {
    const geometry = feature && feature.geometry;

    if (
        !geometry ||
        (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')
    ) {
        return feature;
    }

    const originalVertices = collectOriginalVertices(sourceFeatures);
    const coordinates = geometry.type === 'Polygon'
        ? geometry.coordinates.map(ring => restoreRingVertices(ring, originalVertices))
        : geometry.coordinates.map(polygon => polygon.map(
            ring => restoreRingVertices(ring, originalVertices)
        ));

    return {
        ...feature,
        geometry: {
            ...geometry,
            coordinates
        }
    };
};

const isQuadrilateral = feature => {
    if (!feature || !feature.geometry || feature.geometry.type !== 'Polygon') {
        return false;
    }

    const ring = feature.geometry.coordinates && feature.geometry.coordinates[0];

    if (!Array.isArray(ring)) {
        return false;
    }

    const vertices = ring.slice();

    if (
        vertices.length > 1 &&
        coordinatesAreEqual(vertices[0], vertices[vertices.length - 1])
    ) {
        vertices.pop();
    }

    return vertices.length === 4;
};

const getNumericProperties = features => {
    const propertyNames = [];

    features.forEach(feature => {
        Object.keys((feature && feature.properties) || {}).forEach(name => {
            if (propertyNames.indexOf(name) === -1) {
                propertyNames.push(name);
            }
        });
    });

    return propertyNames.reduce((numericProperties, name) => {
        const values = features.map(feature => {
            const properties = (feature && feature.properties) || {};
            return properties[name];
        });

        if (!values.every(isFiniteNumber)) {
            return numericProperties;
        }

        const numbers = values.map(Number);

        const range = numbers.reduce((result, value) => ({
            min: Math.min(result.min, value),
            max: Math.max(result.max, value)
        }), { min: numbers[0], max: numbers[0] });

        numericProperties.push({
            name,
            min: range.min,
            max: range.max
        });

        return numericProperties;
    }, []);
};

export const getGeometryInfo = features => {
    const validFeatures = (features || []).filter(feature => feature && feature.geometry);
    const polygonFeatures = validFeatures.filter(feature => feature.geometry.type === 'Polygon');
    const quadrilateralCount = polygonFeatures.filter(isQuadrilateral).length;
    const numericProperties = getNumericProperties(polygonFeatures);
    const isPolygonCollection =
        validFeatures.length > 1 &&
        polygonFeatures.length === validFeatures.length;
    const isMatrix =
        isPolygonCollection &&
        quadrilateralCount / polygonFeatures.length >= 0.8 &&
        numericProperties.length > 0;
    const canMerge =
        polygonFeatures.length > 1 &&
        numericProperties.length > 0;

    return {
        type: isMatrix
            ? 'matrix'
            : (polygonFeatures.length === validFeatures.length ? 'polygon' : 'mixed'),
        isMatrix,
        canMerge,
        numericProperties,
        polygonCount: polygonFeatures.length,
        quadrilateralCount
    };
};

const createRangeProperties = (group, propertyName, start, end) => {
    const values = group.map(feature => Number(feature.properties[propertyName]));
    const average = values.reduce((total, value) => total + value, 0) / values.length;
    const roundedAverage = Math.round(average);
    const roundedStart = Math.round(start);
    const roundedEnd = Math.round(end);

    return {
        name: `${roundedStart} - ${roundedEnd}`,
        [propertyName]: roundedAverage,
        average_value: roundedAverage,
        range_min: roundedStart,
        range_max: roundedEnd,
        range_count: group.length,
        poly_type: 'F'
    };
};

const unionRangeGroup = (group, properties) => {
    if (group.length === 1) {
        return {
            ...group[0],
            properties: {
                ...group[0].properties,
                ...properties
            }
        };
    }

    try {
        let merged = group[0];

        for (let index = 1; index < group.length; index++) {
            merged = turf.union(merged, group[index]);
        }

        merged = restoreOriginalBoundaryVertices(merged, group);

        return {
            ...merged,
            properties
        };
    } catch (error) {
        return group.map(feature => ({
            ...feature,
            properties: {
                ...feature.properties,
                ...properties,
                range_merge_error: true
            }
        }));
    }
};

export const mergeAllPolygons = features => {
    const polygons = convertMultiPolygonsToPolygons(features)
        .filter(feature =>
            feature &&
            feature.geometry &&
            feature.geometry.type === 'Polygon'
        );

    if (polygons.length <= 1) {
        return polygons.map(feature => ({
            ...feature,
            properties: {
                ...feature.properties,
                name: 'Limite geral',
                background: true,
                poly_type: 'N'
            }
        }));
    }

    try {
        let merged = polygons[0];

        for (let index = 1; index < polygons.length; index++) {
            merged = turf.union(merged, polygons[index]);
        }

        merged = restoreOriginalBoundaryVertices(merged, polygons);

        return convertMultiPolygonsToPolygons([{
            ...merged,
            properties: {
                name: 'Limite geral',
                background: true,
                poly_type: 'N'
            }
        }]);
    } catch (error) {
        return polygons.map(feature => ({
            ...feature,
            properties: {
                name: 'Limite geral',
                background: true,
                poly_type: 'N',
                merge_error: true
            }
        }));
    }
};

export const mergePolygonsByRange = (features, propertyName, separation) => {
    const source = convertMultiPolygonsToPolygons(features)
        .filter(feature => feature && feature.geometry);
    const numericSeparation = Number(separation);

    if (!Number.isFinite(numericSeparation) || numericSeparation <= 0) {
        return source.map(feature => ({
            ...feature,
            properties: {
                ...feature.properties,
                average_value: isFiniteNumber(feature.properties && feature.properties[propertyName])
                    ? Math.round(Number(feature.properties[propertyName]))
                    : null,
                poly_type: 'F'
            }
        }));
    }

    const polygonFeatures = source.filter(feature =>
        feature.geometry.type === 'Polygon' &&
        feature.properties &&
        isFiniteNumber(feature.properties[propertyName])
    );
    const otherFeatures = source
        .filter(feature => polygonFeatures.indexOf(feature) === -1)
        .map(feature => ({
            ...feature,
            properties: {
                ...feature.properties,
                average_value: isFiniteNumber(feature.properties && feature.properties[propertyName])
                    ? Math.round(Number(feature.properties[propertyName]))
                    : null,
                poly_type: 'F'
            }
        }));

    if (polygonFeatures.length === 0) {
        return otherFeatures;
    }

    const values = polygonFeatures.map(feature => Number(feature.properties[propertyName]));
    const min = values.reduce((result, value) => Math.min(result, value), values[0]);
    const max = values.reduce((result, value) => Math.max(result, value), values[0]);
    const width = numericSeparation;
    const rangeCount = Math.max(1, Math.ceil((max - min) / width));
    const groups = {};

    polygonFeatures.forEach(feature => {
        const value = Number(feature.properties[propertyName]);
        const rawIndex = Math.floor((value - min) / width);
        const rangeIndex = Math.min(rangeCount - 1, Math.max(0, rawIndex));

        if (!groups[rangeIndex]) {
            groups[rangeIndex] = [];
        }

        groups[rangeIndex].push(feature);
    });

    const mergedFeatures = Object.keys(groups)
        .map(Number)
        .sort((first, second) => first - second)
        .reduce((result, rangeIndex) => {
            const start = min + (rangeIndex * width);
            const end = rangeIndex === rangeCount - 1
                ? max
                : Math.min(max, start + width);
            const properties = createRangeProperties(
                groups[rangeIndex],
                propertyName,
                start,
                end
            );
            const merged = unionRangeGroup(groups[rangeIndex], properties);

            return result.concat(merged);
        }, []);

    return convertMultiPolygonsToPolygons(
        mergedFeatures.concat(otherFeatures)
    );
};

export default getGeometryInfo;
